import React, { useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
	CreateCaseRequisition,
	CreateCaseRequisitionVariables,
	CreateDepartmentRequisition,
	CreateDepartmentRequisitionVariables,
	GetVendors,
	GetVendors_vendors,
	RequisitionType,
	GetWebDepartments,
	GetWebRequisitions,
	GetWebRequisitions_requisitions,
	GetWebCase,
	GetSingleCase_case,
	GetVendorsVariables,
	GetCatalogsWithCraftsmen,
	GetCatalogsWithCraftsmenVariables,
	GetWebCasesForCatalogSuggestions,
	GetWebCasesForCatalogSuggestionsVariables,
	GetWebCasesForCatalogSuggestions_cases,
	GetWebRequisitionsVariables,
	GetWebCaseVariables,
} from '../../GraphQL';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateRequisitionSchema } from '../../Schemas/CreateRequisitionSchema';
import { loader } from 'graphql.macro';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import Dropdown from '@ssg/common/Components/Dropdown';
import Input from '@ssg/common/Components/Input';
import Button from '@ssg/common/Components/Button';
import Modal from '@ssg/common/Components/Modal';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import Textarea from '@ssg/common/Components/Textarea';
import Radio from '@ssg/common/Components/Radio';
import Checkbox from '@ssg/common/Components/Checkbox';
import FormFieldHeader from '@ssg/common/Components/FormFieldHeader';
import InputPhone from '@ssg/common/Components/InputPhone';
import EnvironmentVariableContext from '@ssg/common/EnvironmentVariableContext';

const GET_CASES_FOR_CATALOG_SUGGESTIONS = loader('src/GraphQL/Cases/GetWebCasesForCatalogSuggestions.gql');
const GET_DEPARTMENTS = loader('src/GraphQL/Departments/GetWebDepartments.gql');
const GET_VENDORS = loader('src/GraphQL/Requisitions/GetVendors.gql');
const CREATE_CASE_REQUISITION = loader('src/GraphQL/Requisitions/CreateCaseRequisition.gql');
const CREATE_DEPARTMENT_REQUISITION = loader('src/GraphQL/Requisitions/CreateDepartmentRequisition.gql');
const GET_REQUISITIONS = loader('src/GraphQL/Requisitions/GetWebRequisitions.gql');
const GET_CATALOGS_WITH_CRAFTSMEN = loader('src/GraphQL/Catalogs/GetCatalogsWithCraftsmen.gql');
const GET_CASE = loader('src/GraphQL/Cases/GetWebCase.gql');

interface CreateRequisitionVariables {
	requisitionConnectType: string;
	//id: string;
	vendor: string;
	email: string;
	phone: string;
	description: string;
	caseId: string;
	departmentId: string;
	drivingSlipId: string;
	requisitionType: string;
	sendEmail: boolean;
}

interface CraftsmanInfo {
	id: string;
	name: string;
	email: string;
	phone: string;
	remark: string;
	type: string;
	phoneCount: number;
}

interface Props {
	visible: boolean;
	close(): void;
	caseData?: GetSingleCase_case;
	requisitionVariables?: GetWebRequisitionsVariables | undefined;
}

const RequisitionModal: React.FC<Props> = ({ visible, close, caseData, requisitionVariables }): React.ReactElement => {
	const { t } = useTranslation();
	const envVar = useContext(EnvironmentVariableContext);

	const [casesSearchText, setCasesSearchText] = React.useState('');
	const [selectedCase, setSelectedCase] = React.useState<GetWebCasesForCatalogSuggestions_cases | undefined>(caseData);
	const [vendorSearchText, setVendorSearchText] = React.useState('');
	const [selectedVendor, setSelectedVendor] = React.useState<GetVendors_vendors | undefined>(undefined);
	const [selectedCraftsman, setSelectedCraftsman] = React.useState<CraftsmanInfo | undefined>(undefined);
	const [createdRequisition, setCreatedRequisition] = React.useState<GetWebRequisitions_requisitions | undefined>(undefined);
	const [acceptUnknownDebitor, setAcceptUnknownDebitor] = React.useState(true);

	const [country, setCountry] = React.useState(envVar.defaultCountry);
	const [phonenumber, setPhonenumber] = React.useState('');

	const { register, handleSubmit, setValue, control, errors, trigger } = useForm<CreateRequisitionVariables>({
		mode: 'onChange',
		resolver: yupResolver(CreateRequisitionSchema),
		defaultValues: {
			requisitionType: 'SUBCONTRACTOR',
			requisitionConnectType: 'case',
			caseId: caseData?.id,
			phone: '',
		},
	});
	const phonenumberHandler = React.useCallback(
		(phonenumber: string): void => {
			// Assume the phonenumber is danish in order to prefill phone input
			if (!phonenumber.includes('+') && phonenumber.length === 8) {
				phonenumber = '+45' + phonenumber;
			}
			phonenumber = formatPhoneNumberIntl(phonenumber).replaceAll(/(\s|-)+/g, '');

			setValue('phone', phonenumber, { shouldValidate: true });
			setPhonenumber(phonenumber);
		},
		[setValue],
	);

	const requisitionType: string = useWatch({
		control,
		name: 'requisitionType',
		defaultValue: 'SUBCONTRACTOR',
	});
	const selectedType: string = useWatch({
		control,
		name: 'requisitionConnectType',
		defaultValue: 'case',
	});

	const { data: cases, loading: loadingCases } = useQuery<GetWebCasesForCatalogSuggestions, GetWebCasesForCatalogSuggestionsVariables>(GET_CASES_FOR_CATALOG_SUGGESTIONS, {
		variables: { erpReferenceNo: casesSearchText },
		skip: casesSearchText.length === 0 || typeof caseData !== 'undefined',
	});

	const { data: departments } = useQuery<GetWebDepartments>(GET_DEPARTMENTS);
	const { data: vendors } = useQuery<GetVendors, GetVendorsVariables>(GET_VENDORS, {
		variables: {
			company: vendorSearchText,
		},
		skip: vendorSearchText.length === 0,
	});

	const { data: catalogsData, loading: loadingCatalogsData } = useQuery<GetCatalogsWithCraftsmen, GetCatalogsWithCraftsmenVariables>(GET_CATALOGS_WITH_CRAFTSMEN, {
		variables: {
			address: {
				road: selectedCase?.damage.contact.address.road.trim() ?? '',
				houseNumber: selectedCase?.damage.contact.address.houseNumber.trim() ?? '',
				addressLineAlt: selectedCase?.damage.contact.address.addressLineAlt?.trim() ?? '',
				postalCode: selectedCase?.damage.contact.address.postalCode.trim() ?? '',
				city: selectedCase?.damage.contact.address.city.trim() ?? '',
				country: selectedCase?.damage.contact.address.country.trim() ?? '',
			},
			debitors: selectedCase !== undefined ? [selectedCase.debitor.id] : null,
		},
		skip: typeof selectedCase === 'undefined' || requisitionType !== 'SUBCONTRACTOR',
	});

	const catalogCraftsmen = React.useMemo(() => catalogsData?.catalogs?.flatMap(c => c.craftsmen) ?? [], [catalogsData?.catalogs]);

	const craftsmenInfo: CraftsmanInfo[] = React.useMemo(
		() =>
			catalogCraftsmen.map(cc => {
				const informations = cc.informations.slice();
				const informationsCount = informations.slice().length;
				if (informations.length === 0) {
					informations.push({ phoneNumber: '', remark: '' });
				}

				return {
					id: cc.id,
					name: cc.contactName,
					email: cc.email ?? '',
					phone: informations[0].phoneNumber,
					remark: informations[0].remark ?? '',
					type: cc.type,
					phoneCount: informationsCount,
				};
			}),
		[catalogCraftsmen],
	);

	React.useEffect(() => {
		if (typeof selectedCraftsman === 'undefined') {
			setValue('email', '', { shouldValidate: false });
			setValue('vendor', '', { shouldValidate: false });
			setVendorSearchText('');
			phonenumberHandler('');
		} else {
			setValue('email', selectedCraftsman.email, {
				shouldValidate: true,
			});
			setValue('vendor', selectedCraftsman.name, {
				shouldValidate: true,
			});
			setVendorSearchText(selectedCraftsman.name);
			phonenumberHandler(selectedCraftsman.phone ?? '');
		}
	}, [phonenumberHandler, selectedCraftsman, setValue]);

	const [createCaseRequisition, { loading: createCaseRequisitionLoading }] = useMutation<CreateCaseRequisition, CreateCaseRequisitionVariables>(CREATE_CASE_REQUISITION);
	const [createDepartmentRequisition, { loading: createDepartmentRequisitionLoading }] = useMutation<CreateDepartmentRequisition, CreateDepartmentRequisitionVariables>(
		CREATE_DEPARTMENT_REQUISITION,
	);

	React.useEffect(() => {
		setValue('email', selectedVendor?.email);
		phonenumberHandler(selectedVendor?.phone ?? '');
	}, [phonenumberHandler, selectedVendor, setValue]);

	React.useEffect(() => {
		if (!visible) {
			setCreatedRequisition(undefined);
			typeof caseData === 'undefined' && setSelectedCase(undefined);
			typeof caseData === 'undefined' && setCasesSearchText('');
			setSelectedCraftsman(undefined);
		}
	}, [caseData, visible]);

	React.useEffect(() => setSelectedCase(caseData), [caseData]);

	React.useEffect(() => setAcceptUnknownDebitor(!selectedCase?.debitor.unknown), [selectedCase]);

	const onSubmit = async (data: CreateRequisitionVariables) => {
		switch (data.requisitionConnectType) {
			case 'case':
				try {
					await postCreateCaseRequisition({
						caseId: data.caseId,
						requisition: {
							description: data.description,
							type: data.requisitionType as RequisitionType,
							vendorName: data.vendor,
							vendorEmail: data.email,
							vendorNo: selectedVendor?.erpReferenceNo,
							vendorPhoneNumber: data.phone,
						},
						shouldSendEmail: data.sendEmail,
					});
				} catch (e) {
					console.log(e);
				}
				break;
			case 'department':
				try {
					await postCreateDepartmentRequisition({
						departmentId: data.departmentId,
						requisition: {
							description: data.description,
							type: data.requisitionType as RequisitionType,
							vendorName: data.vendor,
							vendorEmail: data.email,
							vendorNo: selectedVendor?.erpReferenceNo,
							vendorPhoneNumber: data.phone,
						},
						shouldSendEmail: data.sendEmail,
					});
				} catch (e) {
					console.log(e);
				}
				break;
		}
	};

	const postCreateCaseRequisition = async ({ caseId, requisition, shouldSendEmail }: CreateCaseRequisitionVariables) => {
		await createCaseRequisition({
			variables: {
				caseId,
				requisition,
				shouldSendEmail,
			},
			update: (cache, { data }): void => {
				if (typeof caseData === 'undefined') {
					if (typeof data === 'undefined' || data === null) {
						return;
					}
					setCreatedRequisition(data.createCaseRequisition);

					// Update cache
					const cachedRequestReqs = cache.readQuery<GetWebRequisitions, GetWebRequisitionsVariables>({
						query: GET_REQUISITIONS,
						variables: {
							...requisitionVariables,
						},
					});

					if (cachedRequestReqs !== null) {
						cache.writeQuery({
							query: GET_REQUISITIONS,
							variables: {
								...requisitionVariables,
							},
							data: {
								requisitions: [data.createCaseRequisition, ...cachedRequestReqs.requisitions],
							},
						});
					}
				}

				if (typeof caseData !== 'undefined') {
					if (typeof data === 'undefined' || data === null) {
						return;
					}
					setCreatedRequisition(data.createCaseRequisition);

					const cachedRequestCase = cache.readQuery<GetWebCase, GetWebCaseVariables>({
						query: GET_CASE,
						variables: {
							id: caseData.id,
						},
					});

					if (cachedRequestCase === null) {
						return;
					}

					cache.writeQuery({
						query: GET_CASE,
						variables: {
							id: caseData.id,
						},
						data: {
							case: {
								...cachedRequestCase.case,
								requisitions: [data.createCaseRequisition, ...cachedRequestCase.case.requisitions],
							},
						},
					});
				}
			},
		});
	};

	const postCreateDepartmentRequisition = async ({ departmentId, requisition, shouldSendEmail }: CreateDepartmentRequisitionVariables) => {
		await createDepartmentRequisition({
			variables: {
				departmentId,
				requisition,
				shouldSendEmail,
			},
			update: (cache, { data }): void => {
				if (typeof data === 'undefined' || data === null) {
					return;
				}
				setCreatedRequisition(data.createDepartmentRequisition);

				// Update cache
				const cachedRequestMine = cache.readQuery<GetWebRequisitions, GetWebRequisitionsVariables>({
					query: GET_REQUISITIONS,
					variables: {
						myRequisitionsOnly: true,
					},
				});

				if (cachedRequestMine !== null) {
					cache.writeQuery({
						query: GET_REQUISITIONS,
						variables: {
							myRequisitionsOnly: true,
						},
						data: {
							requisitions: [...cachedRequestMine.requisitions, data.createDepartmentRequisition],
						},
					});
				}

				const cachedRequestAll = cache.readQuery<GetWebRequisitions, GetWebRequisitionsVariables>({
					query: GET_REQUISITIONS,
					variables: {
						myRequisitionsOnly: false,
					},
				});

				if (cachedRequestAll !== null) {
					cache.writeQuery({
						query: GET_REQUISITIONS,
						variables: {
							myRequisitionsOnly: false,
						},
						data: {
							requisitions: [...cachedRequestAll.requisitions, data.createDepartmentRequisition],
						},
					});
				}
			},
		});
	};

	return (
		<Modal
			title="requisitions.createRequisition"
			visible={visible}
			close={close}
			body={
				<>
					{typeof createdRequisition === 'undefined' ? (
						<form onSubmit={handleSubmit(onSubmit)}>
							<Radio
								name="requisitionType"
								title="common.type"
								options={[
									{
										label: 'common.subcontractor',
										value: 'SUBCONTRACTOR',
									},
									{
										label: 'common.materiale',
										value: 'MATERIAL',
									},
								]}
								dValue={'SUBCONTRACTOR'}
								innerRef={register}
							/>

							<Radio
								name="requisitionConnectType"
								title="requisitions.requisitionTo"
								options={[
									{ label: 'common.case', value: 'case' },
									{
										label: 'common.department',
										value: 'department',
									},
								]}
								dValue={selectedType}
								innerRef={register}
								disabled={typeof caseData !== 'undefined'}
							/>

							{selectedType === 'department' && (
								<Dropdown
									name="departmentId"
									title="common.department"
									onBlur={() => undefined}
									data={[
										{ label: '', value: '' },
										...(departments?.departments ?? [])
											.slice()
											.sort((a, b) => a.name.localeCompare(b.name))
											.map(l => ({
												label: l.name,
												value: l.id,
											})),
									]}
									innerRef={register}
									required
									errorMessage={errors.departmentId?.message}
								/>
							)}

							{selectedType === 'case' && (
								<SearchableSelect
									name="caseId"
									title="common.case"
									searchFn={searchText => setCasesSearchText(searchText)}
									onSelect={value => {
										setSelectedCase(cases?.cases.find(c => c.id === value));
										setValue('caseId', value, {
											shouldValidate: true,
										});
									}}
									onBlur={() => undefined}
									minInputLength={2}
									options={(cases?.cases ?? []).map(c => ({
										label: c.erpNo,
										value: c.id,
									}))}
									isLoading={loadingCases}
									control={control}
									required
									initialSelection={
										typeof caseData !== 'undefined'
											? {
												label: caseData.erpNo,
												value: caseData.id,
											}
											: undefined
									}
									errorMessage={errors.caseId?.message}
									readOnly={typeof caseData !== 'undefined'}
								/>
							)}

							{selectedCase?.debitor.unknown && (
								<div className="border-1 border-blue rounded-b-default bg-blue z-50 -mt-1 border-t-0 bg-opacity-95 p-1">
									<p className="font-medium text-white">{t('requisitions.caseNoDebitor')}</p>
									<Checkbox
										name="createAnyway"
										title="requisitions.acceptNoDebitor"
										className="text-sm text-white"
										defaultChecked={false}
										onChange={e => setAcceptUnknownDebitor(e.currentTarget.checked)}
										textClassName="text-white"
									/>
								</div>
							)}

							{requisitionType === 'SUBCONTRACTOR' && (
								<div>
									<FormFieldHeader title="requisitions.catalogSuggestions" />
									{loadingCatalogsData ? (
										<FontAwesomeIcon icon={faSpinner} className="text-blue animate-spin" size="lg" />
									) : (
										<>
											{craftsmenInfo?.length > 0 ? (
												<Dropdown
													title=""
													name=""
													data={[
														{
															label: `${craftsmenInfo.length.toString()} ${t('requisitions.options')}`,
															value: '',
														},
														...craftsmenInfo.map(ci => ({
															label: `${ci.name} - ${ci.type} (${ci.phoneCount} ${t('common.tlf')})`,
															value: ci.id,
														})),
													]}
													onChange={e =>
														setSelectedCraftsman(selectedCraftsman?.id !== e.currentTarget.value ? craftsmenInfo.find(ci => ci.id === e.currentTarget.value) : undefined)
													}
													disableSortLabelAlphabetically
												/>
											) : (
												<div>{t('common.none')}</div>
											)}
										</>
									)}
								</div>
							)}

							<SearchableSelect
								name="vendor"
								title="common.subcontractor"
								searchFn={searchText => {
									setVendorSearchText(searchText);
									setValue('vendor', searchText);
								}}
								onSelect={value => {
									setSelectedVendor(vendors?.vendors.find(v => v.erpReferenceNo === value));
									setValue('vendor', vendors?.vendors.find(v => v.erpReferenceNo === value)?.company ?? '', { shouldValidate: true });
								}}
								onBlur={() => undefined}
								minInputLength={2}
								options={
									vendors?.vendors.map(v => ({
										label: v.company ?? '',
										value: v.erpReferenceNo,
									})) ?? []
								}
								isLoading={loadingCases}
								control={control}
								required
								errorMessage={errors.vendor?.message}
								readOnly={typeof selectedCraftsman !== 'undefined'}
							/>

							<InputPhone
								title="requisitions.vendorPhone"
								innerRef={register}
								required
								name="phone"
								errorMessage={errors.phone?.message}
								control={control}
								trigger={trigger}
								searchFn={(number): void => phonenumberHandler(number)}
								onCountryChange={(countrycode): void => setCountry(countrycode ?? '')}
								defaultCountry={country}
								defaultPhoneNumber={phonenumber}
							/>

							<Input title="requisitions.vendorEmail" name="email" required innerRef={register} errorMessage={errors.email?.message} className="w-full lg:w-full " />

							<Checkbox name="sendEmail" title="requisitions.sendEmail" innerRef={register} className="mt-2" />

							<Textarea title="common.description" name="description" required innerRef={register} errorMessage={errors.description?.message} className="h-32 w-full lg:w-full" />

							<div className="mt-2">
								<Button
									text="requisitions.createRequisition"
									success
									submit
									disabled={!acceptUnknownDebitor}
									loading={createCaseRequisitionLoading || createDepartmentRequisitionLoading}
								/>
							</div>
						</form>
					) : (
						<div className="text-blue text-center">
							<p className="text-green text-lg font-medium">{t('requisitions.created')}!</p>
							<p>{t('requisitions.requisitionId')}:</p>
							<p className="text-2xl font-medium">{createdRequisition.orderNumber}</p>
						</div>
					)}
				</>
			}
		/>
	);
};

export default RequisitionModal;
